'use client';
import React, { useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import { useCustomRouter } from '@benzinga/hooks';
import { useUser } from '@benzinga/user-context';
import { MetaProps } from '@benzinga/seo';
import { getDocumentReferrer, setNewDocumentReferrer } from './utils';
import { ContentManager, isContentHasReachImpressionLimit } from '@benzinga/content-manager';
import { Session, User } from '@benzinga/session';
import { SessionContext, SessionPropsExtension } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { InView } from 'react-intersection-observer';

const RouteChangeListenerAppRouter = React.lazy(() =>
  import('./RouteChangeListenerAppRouter').then(module => {
    return { default: module.RouteChangeListenerAppRouter };
  }),
);

const RouteChangeListenerPageRouter = React.lazy(() =>
  import('./RouteChangeListenerPageRouter').then(module => {
    return { default: module.RouteChangeListenerPageRouter };
  }),
);

export type PageViewEventProperties = { page_tab?: string; page_section?: string };

interface Props {
  enableCoralogixRum?: boolean;
  getViewEventProperties?: (pathname: string) => PageViewEventProperties;
  routerType?: 'page' | 'app';
}

// This is the way we did it in Drupal.  Should we still?  IDK.  We can also skip this or lave..... and just rely on the DataLake to solve problems.
export const getAgeOfArticleByCreatedDate = (dateCreated?: string) => {
  let ageOfArticle: number | null = null;

  if (dateCreated) {
    const today = Date.now() / 1000;
    const timestamp = DateTime.fromISO(dateCreated).toMillis() / 1000;
    const numberOfDays = (today - timestamp) / (60 * 60 * 24);

    if (numberOfDays > 2000) {
      ageOfArticle = 10000;
    } else {
      const rangeArray = [0, 1, 2, 3, 5, 7, 10, 15, 20, 30, 60, 90, 180, 365, 730, 1000, 2000];
      for (const i in rangeArray) {
        if (numberOfDays <= rangeArray[i] && numberOfDays > rangeArray[parseInt(i) - 1]) {
          return (ageOfArticle = rangeArray[i]);
        }
      }
    }
  }

  return ageOfArticle;
};

export const clearUTMKeys = (): string => {
  const params = new URLSearchParams(window.location.search);

  if (params) {
    const keys = Array.from(Object(params).keys()) as string[];
    if (keys.length) {
      keys.forEach(key => {
        if (key.includes('utm_')) {
          params.delete(key);
        }
      });

      const cleanedParams = params.toString();

      const query = cleanedParams ? '?' + cleanedParams : '';
      const cleanUrl = window.location.pathname + query + window.location.hash;

      if (cleanUrl) {
        window.history.replaceState(null, '', cleanUrl);

        return cleanUrl;
      }
    }
  }

  return window.location.href;
};

export const runTrackPage = (session?: Session, properties: PageViewEventProperties = {}) => {
  const pageParams = {
    path: window.location.pathname,
  };

  const referrer = getDocumentReferrer();
  if (referrer) {
    pageParams['referrer'] = referrer;
  }

  session?.getManager(TrackingManager).trackPageEvent('view', properties);
  return true;
};

export function runIdentify(session: Session, user: User) {
  if (user.accessType === 'anonymous') return;
  session.getManager(TrackingManager).identify(user.benzingaUid, {
    email: user.email,
    iam_uuid: user.uuid,
  });
}

let lastTrackedPathnameModule: string | null = null;

export const resetLastTrackedPathname = () => {
  lastTrackedPathnameModule = null;
};

export const PageTracker: React.FC<Props> = ({ enableCoralogixRum, getViewEventProperties, routerType = 'page' }) => {
  const session: SessionPropsExtension = React.useContext(SessionContext);
  const router = useCustomRouter();

  const user = useUser();

  const runAnalytics = useCallback(() => {
    const currentPathname = window.location.pathname + window.location.search;
    if (lastTrackedPathnameModule === currentPathname) return;
    if (!window?.location?.pathname) return;
    runTrackPage(session.getSession(), getViewEventProperties?.(window.location.pathname) ?? {});
    lastTrackedPathnameModule = currentPathname;
  }, [getViewEventProperties, session]);

  useEffect(() => {
    if (user && enableCoralogixRum) {
      import('@coralogix/browser').then(module => {
        module.CoralogixRum.setUserContext({
          user_email: user.email,
          user_id: String(user.benzingaUid),
          user_metadata: { access_type: user.accessType },
          user_name: user.displayName,
        });
      });
    }
  }, [enableCoralogixRum, user]);

  const handleRouteChange = useCallback(() => {
    runAnalytics();
  }, [runAnalytics]);

  // Detect route change and run analytics
  useEffect(() => {
    const onRouteChange = () => {
      runAnalytics();
    };

    router.events.on('routeChangeComplete', onRouteChange);
    return () => {
      router.events.off('routeChangeComplete', onRouteChange);
    };
  }, [router.events, runAnalytics]);

  return routerType === 'app' ? (
    <RouteChangeListenerAppRouter onRouteChange={handleRouteChange} />
  ) : (
    <RouteChangeListenerPageRouter onRouteChangeComplete={handleRouteChange} />
  );
};

interface VisiblePageTrackerProps {
  article: any;
  executePageviewEvent?: boolean; // used to prevent article tracking triggering more than once
  executeReplaceHistory?: boolean; // used to prevent article url history from updating
  executeImpressionEvent?: boolean;
  isSponsored?: boolean; // modifies google event
  meta?: MetaProps;
  onImpress?: () => void;
  trackMeta?: boolean;
}

export const VisiblePageTracker: React.FC<React.PropsWithChildren<VisiblePageTrackerProps>> = ({
  article,
  children,
  executeImpressionEvent = true,
  executePageviewEvent = true,
  executeReplaceHistory = true,
  meta,
  onImpress,
  trackMeta = true,
}) => {
  const session: SessionPropsExtension = React.useContext(SessionContext);
  const [didImpress, setDidImpress] = React.useState(false);

  const handleIsVisible = React.useCallback(
    (isVisible: boolean) => {
      if (isVisible && executeReplaceHistory) {
        const path = `/${article.canonicalPath}${window.location.search}`;
        if (article.canonicalPath && path !== window.location.pathname) {
          setNewDocumentReferrer(window.location.href);
          window.history.replaceState({}, article.title, path);
        }

        const event = new CustomEvent('page-transition', { detail: { meta, trackMeta } });
        window.dispatchEvent(event);
      }

      if (isVisible && !didImpress && executeImpressionEvent) {
        session.getManager(TrackingManager).trackCampaignEvent('view', {
          partner_id: `${article.id}-${article.id}`,
          sponsored: article.isSponsored,
          unit_type: `visible-page-tracker-${article.id}`,
        });

        if (isContentHasReachImpressionLimit(article?.meta)) {
          session
            .getManager(ContentManager)
            .registerContentImpression(article.nodeId)
            .catch(error => console.log('Register Content Impression Error:', error));
        }

        // gtag('event', 'search', {
        //   'search_term': query
        // });

        // 5 second delay to run impression/page track, also confirming that the current article is still in view
        setTimeout(() => {
          if (window.location.pathname === `/${article.canonicalPath}`) {
            setDidImpress(true);

            if (onImpress) {
              onImpress();
            }
          }
        }, 50);
      }
    },
    [
      executeImpressionEvent,
      executeReplaceHistory,
      didImpress,
      article.canonicalPath,
      article.title,
      article.isSponsored,
      meta,
      onImpress,
      trackMeta,
      article.id,
      article.meta,
      article.nodeId,
      session,
    ],
  );

  return (
    <InView onChange={handleIsVisible}>
      <>
        {didImpress && executePageviewEvent && <PageTracker />}
        {children}
      </>
    </InView>
  );
};
